/**
 * The one request path of the workspace API client: the failure model, the
 * CSRF token, the dead-session listener and idempotency keys.
 *
 * One module rather than fetches scattered through components, for the same
 * reason the server keeps SQL in one package: the security-relevant details —
 * the CSRF header, `credentials: 'same-origin'`, the idempotency key, how a
 * problem document becomes an error — live here once and cannot drift. The
 * resource modules beside this file build every request on it.
 *
 * Errors are normalised into {@link ApiFailure} with a `kind` the UI can
 * switch on. The distinction that matters most is `offline` versus `conflict`
 * versus `forbidden` versus `missing`: each one is a different sentence to a
 * person, and collapsing them is how "the network blipped" gets rendered as
 * "you are not allowed".
 */

/** The header a browser presents its session-bound CSRF token in. */
const CSRF_HEADER = 'x-csrf-token';
const IDEMPOTENCY_HEADER = 'idempotency-key';

export type FailureKind =
  /** No network, or the request never reached the service. */
  | 'offline'
  /** 401 — the session is gone. The shell redirects to sign-in. */
  | 'unauthenticated'
  /** 403 — signed in, but this action is not theirs to take. */
  | 'forbidden'
  /** 404 — missing or not visible; the two are indistinguishable by design. */
  | 'missing'
  /** 409 — the resource moved, or an idempotency key was reused. */
  | 'conflict'
  /** 400 — the request was malformed; `detail` says what to fix. */
  | 'invalid'
  /** 429 — throttled; `retryAfterSeconds` says when to try again. */
  | 'rate_limited'
  /** Anything else the service said. */
  | 'failed';

export class ApiFailure extends Error {
  readonly kind: FailureKind;
  readonly status: number | null;
  readonly detail: string | null;
  readonly retryAfterSeconds: number | null;

  constructor(
    kind: FailureKind,
    options: {
      status?: number | null;
      detail?: string | null;
      retryAfterSeconds?: number | null;
    } = {},
  ) {
    super(options.detail ?? kind);
    this.name = 'ApiFailure';
    this.kind = kind;
    this.status = options.status ?? null;
    this.detail = options.detail ?? null;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }
}

function kindOf(status: number): FailureKind {
  switch (status) {
    case 400:
      return 'invalid';
    case 401:
      return 'unauthenticated';
    case 403:
      return 'forbidden';
    case 404:
      return 'missing';
    case 409:
      return 'conflict';
    case 429:
      return 'rate_limited';
    default:
      return 'failed';
  }
}

async function failureFrom(response: Response): Promise<ApiFailure> {
  let detail: string | null = null;
  let retryAfterSeconds: number | null = null;
  try {
    const body = (await response.json()) as { detail?: unknown; retry_after?: unknown };
    if (typeof body.detail === 'string') detail = body.detail;
    if (typeof body.retry_after === 'number') retryAfterSeconds = body.retry_after;
  } catch {
    // A refusal without a parseable body is still a refusal.
  }
  return new ApiFailure(kindOf(response.status), {
    status: response.status,
    detail,
    retryAfterSeconds,
  });
}

/** The one place a CSRF token is remembered between requests. */
let csrfToken: string | null = null;

export function rememberCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * The one response to a dead session, wherever it is discovered.
 *
 * Any request — read or mutation — that comes back 401 means the session is
 * gone: expired, revoked, or the account was suspended or deauthorized while
 * the tab was open. The registered listener (the `SessionProvider`) clears
 * the client's authenticated state *before* the failure propagates, so by
 * the time a component reacts, `RequireSession` is already redirecting to
 * sign-in with the current location preserved — and the sign-in page, seeing
 * an anonymous session, stays put instead of bouncing back to /dashboard.
 * Without this ordering the old state produced exactly that loop.
 *
 * `/auth/session` is exempt: there a 401 is the ordinary answer to "is
 * anybody signed in", not a discovery.
 */
let unauthorizedListener: (() => void) | null = null;

export function onSessionUnauthorized(listener: (() => void) | null): void {
  unauthorizedListener = listener;
}

/**
 * The one request path.
 *
 * Exported so a build that adds its own panels can reach the same CSRF
 * header, the same credentials mode, the same idempotency handling, and the
 * same failure normalisation, rather than growing a second fetch with its own
 * subtly different rules.
 */
export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH',
  url: string,
  options: { body?: unknown; idempotencyKey?: string; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken !== null) headers[CSRF_HEADER] = csrfToken;
  if (options.idempotencyKey !== undefined) {
    headers[IDEMPOTENCY_HEADER] = options.idempotencyKey;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ApiFailure('offline');
  }

  if (!response.ok) {
    const failure = await failureFrom(response);
    if (failure.kind === 'unauthenticated' && !url.startsWith('/auth/session')) {
      rememberCsrfToken(null);
      unauthorizedListener?.();
    }
    throw failure;
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** A fresh idempotency key. One per *intent*, reused across retries of it. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
